import sortBy from "lodash/sortBy";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Redirect } from "react-router-dom";

import { MemberSetModel } from "@app/members";
import { TemplateSpecialtyGroup } from "@app/templates";
import { UserContextProps } from "../../user";
import WrappedCreateMemberTable from "./CreateMemberTable";
import CreateProject from "./createProject";
import { SpecialtyOverrides } from "./interfaces";
import WrappedMembershipBindingTable from "./MembershipBindingTable";
import WizardStep from "./WizardStep";

interface CreateProjectStepMembersProps extends RouteComponentProps {
  alternateMemberships: MemberSetModel[];
  baseUrl: string;
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  deleteAltMembershipRow: (memberPop: number) => void;
  disableMemberBindingTable: boolean;
  generateAlternateMemberships: () => void;
  handleMemberPopAliasChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopAliasChange2: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleMemberPopChange2: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handlePrimaryMembershipBindingChange: (memberPop: number, code: string) => void;
  handleSecondaryMembershipBindingChange: (memberPop: number, code: string) => void;
  handleRiskPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleSpecialtyGroupsChange: (selectedRecords: TemplateSpecialtyGroup[]) => void;
  isZipProject: boolean;
  onNextClick: () => void;
  progressList: string[];
  projectMemberPopulations: MemberSetModel[];
  specialtyGroups: TemplateSpecialtyGroup[];
  specialtyOverrides: SpecialtyOverrides[];
  setPrimarySpecialtyOverride: (memberPop: number, code: string) => void;
  setSecondarySpecialtyOverride: (memberPop: number, code: string) => void;

  handleAlternativeMemberPopAliasChange: (alias: string, memberPopId: number) => void;
  handleAlternativeMemberPopChange: (memberPopId: number, prevMemberPopId: number) => void;
  handleNewAlternativeMemberPop: () => void;
  currentIndex: number;
}

interface CreateProjectStepMembersState {
  membershipGroups: MemberSetModel[];
  restartApp: boolean;
  sortedSpecialtyGroups: TemplateSpecialtyGroup[];
}

export default class CreateProjectStepMembers extends React.Component<CreateProjectStepMembersProps & UserContextProps, CreateProjectStepMembersState> {
  private readonly sortOrder: Array<keyof TemplateSpecialtyGroup> = ["displayOrder"];

  constructor(props: CreateProjectStepMembersProps & UserContextProps) {
    super(props);
    this.state = {
      membershipGroups: [],
      restartApp: !props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step),
      sortedSpecialtyGroups: props.specialtyGroups && sortBy(props.specialtyGroups, this.sortOrder) || props.specialtyGroups,
    };
  }

  public componentDidUpdate(prevProps: CreateProjectStepMembersProps) {
    if (this.props.specialtyGroups !== prevProps.specialtyGroups) {
      this.setState({
        sortedSpecialtyGroups: this.props.specialtyGroups && sortBy(this.props.specialtyGroups, this.sortOrder) || this.props.specialtyGroups,
      });
    }
  }

  public render() {
    if (this.state.restartApp) {
      return <Redirect to={`${this.props.baseUrl}/1`} />;
    }

    return (
      <WizardStep {...this.props} heading="Create a New Project" subheading="Define Analysis :: Members" baseUrl={this.props.baseUrl} prevUrl={(this.props.currentIndex - 1).toString()} nextUrl={(this.props.currentIndex + 1).toString()} progressList={this.props.progressList} isNextDisabled={this.handleIsNextDisabled()} onNextClick={this.props.onNextClick}>
        {this.props.projectMemberPopulations && this.props.projectMemberPopulations.length > 0 &&
          <div className="form-group row">
            <div className="col-11">
              <WrappedCreateMemberTable
                createProjectModel={this.props.createProjectModel}
                deleteAltMembershipRow={this.props.deleteAltMembershipRow}
                generateAlternateMemberships={this.props.generateAlternateMemberships}
                handleAlternativeMemberPopAliasChange={this.props.handleAlternativeMemberPopAliasChange}
                handleAlternativeMemberPopChange={this.props.handleAlternativeMemberPopChange}
                handleNewAlternativeMemberPop={this.props.handleNewAlternativeMemberPop}
                handleMemberPopAliasChange={this.props.handleMemberPopAliasChange}
                handleMemberPopAliasChange2={this.props.handleMemberPopAliasChange2}
                handleMemberPopChange={this.props.handleMemberPopChange}
                handleMemberPopChange2={this.props.handleMemberPopChange2}
                handleRiskPopChange={this.props.handleRiskPopChange}
                isZipProject={this.props.isZipProject}
                projectMemberPopulations={this.props.projectMemberPopulations}
              />
            </div>
          </div>
        }
        {this.state.sortedSpecialtyGroups &&
          this.state.sortedSpecialtyGroups.length > 0 &&
          !this.props.disableMemberBindingTable &&
          (
            <div className="form-group row">
              <div className="col-11">
                <WrappedMembershipBindingTable
                  alternateMemberships={this.props.alternateMemberships}
                  canEdit={true}
                  createProjectModel={this.props.createProjectModel}
                  excludedSpecialtyGroupCodes={this.props.createProjectModel.excludedSpecialtyGroupCodes}
                  specialtyGroups={this.props.specialtyGroups}
                  uniqueKey={`${this.props.createProjectModel.adequacyTemplateCollectionId}${this.props.createProjectModel.adequacyTemplateCorrelationId}`}
                  projectMemberPopulations={this.props.projectMemberPopulations}
                  handlePrimaryMembershipBindingChange={this.props.handlePrimaryMembershipBindingChange}
                  handleSecondaryMembershipBindingChange={this.props.handleSecondaryMembershipBindingChange}
                  setPrimarySpecialtyOverride={this.props.setPrimarySpecialtyOverride}
                  setSecondarySpecialtyOverride={this.props.setSecondarySpecialtyOverride}
                  specialtyOverrides={this.props.specialtyOverrides}
                  user={this.props.user}
                />
              </div>
            </div>
          )}
      </WizardStep>
    );
  }

  private handleIsNextDisabled() {
    const adequacyTemplateComplete = (!!this.props.createProjectModel.adequacyTemplateCollectionId &&
      !!this.props.createProjectModel.adequacyTemplateCorrelationId &&
      this.props.createProjectModel.adequacyTemplateCorrelationId !== "");
    const accuracyTemplateComplete = (!!this.props.createProjectModel.accuracyTemplateCollectionId &&
      !!this.props.createProjectModel.accuracyTemplateCorrelationId &&
      this.props.createProjectModel.accuracyTemplateCorrelationId !== "");
    const memberSetIdSelected = !!this.props.createProjectModel.memberSetId;
    const memberSetsWithAliases = this.props.createProjectModel.createProjectOverrideMemberSets.filter((oms) => !!oms.alias);
    const memberSetAliases = new Set(memberSetsWithAliases.map((oms) => oms.alias));
    return ((!accuracyTemplateComplete || adequacyTemplateComplete) && !memberSetIdSelected)
      || (!adequacyTemplateComplete && !accuracyTemplateComplete)
      || (adequacyTemplateComplete && memberSetsWithAliases.length !== memberSetAliases.size);
  }

  private sortArrayAfterFirstItem = (arr: MemberSetModel[]): MemberSetModel[] => {
    let newArr = arr;
    const firstItem = newArr.shift();
    newArr = newArr.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1);
    newArr.unshift(firstItem);
    return newArr;
  }
}
